import { Controller } from "@hotwired/stimulus"
import * as XLSX from "xlsx"

export default class extends Controller {
  static targets = ["dialog", "preview", "sheetTabs", "title"]

  connect() {
    this.dialogTarget.addEventListener("click", (e) => {
      if (e.target === this.dialogTarget) this.dialogTarget.close()
    })

    if (this.dialogTarget.open) this.dialogTarget.close()
  }

  async open(event) {
    event.preventDefault()
    const button = event.currentTarget
    const fileUrl = button.dataset.fileUrl
    const filename = button.dataset.filename
    const importUrl = button.dataset.importUrl

    this.importUrl = importUrl
    this.importMethod = button.dataset.importMethod || "get"
    this.titleTarget.textContent = filename
    this.previewTarget.innerHTML = this.loadingHtml()
    this.sheetTabsTarget.innerHTML = ""
    this.dialogTarget.showModal()

    try {
      const response = await fetch(fileUrl)
      const buffer = await response.arrayBuffer()
      this.workbook = XLSX.read(buffer, { type: "array", cellStyles: true })
      this.preferredSheets = JSON.parse(button.dataset.preferredSheets || "[]")
      this.renderSheetTabs()
      this.selectSheet(this.findPreferredSheet())
    } catch (e) {
      this.previewTarget.innerHTML = `<p class="text-red-600 py-8 text-center">Excel ファイルの読み込みに失敗しました</p>`
    }
  }

  close() {
    this.dialogTarget.close()
  }

  findPreferredSheet() {
    const names = this.workbook.SheetNames
    for (const pref of this.preferredSheets || []) {
      const found = names.find(n => n === pref)
      if (found) return found
    }
    return names[0]
  }

  goToImport() {
    if (this.importMethod === "post") {
      const form = document.createElement("form")
      form.method = "post"
      form.action = this.importUrl
      const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
      if (csrfToken) {
        const input = document.createElement("input")
        input.type = "hidden"
        input.name = "authenticity_token"
        input.value = csrfToken
        form.appendChild(input)
      }
      document.body.appendChild(form)
      form.submit()
    } else {
      window.location.href = this.importUrl
    }
  }

  renderSheetTabs() {
    const names = this.workbook.SheetNames
    const tabs = names.map(name => {
      const escaped = this.escapeHtml(name)
      return `<button type="button" data-sheet-name="${escaped}" data-action="click->import-preview-modal#switchSheet" class="sheet-tab px-3 py-1.5 text-xs font-medium border-b border-r border-gray-300 first:border-l transition-colors cursor-pointer bg-gray-100 text-gray-600 hover:bg-gray-50">${escaped}</button>`
    }).join("")
    this.sheetTabsTarget.innerHTML = `<div class="flex items-center overflow-x-auto bg-gray-50 border-t border-gray-300 px-2 py-1.5 gap-0">${tabs}</div>`
  }

  switchSheet(event) {
    event.preventDefault()
    this.selectSheet(event.currentTarget.dataset.sheetName)
  }

  selectSheet(sheetName) {
    this.currentSheet = this.workbook.Sheets[sheetName]

    this.sheetTabsTarget.querySelectorAll(".sheet-tab").forEach(tab => {
      if (tab.dataset.sheetName === sheetName) {
        tab.classList.remove("bg-gray-100", "text-gray-600", "hover:bg-gray-50")
        tab.classList.add("bg-white", "text-indigo-700", "font-semibold")
      } else {
        tab.classList.remove("bg-white", "text-indigo-700", "font-semibold")
        tab.classList.add("bg-gray-100", "text-gray-600", "hover:bg-gray-50")
      }
    })

    this.renderPreview()
  }

  renderPreview() {
    const sheet = this.currentSheet
    if (!sheet || !sheet["!ref"]) {
      this.previewTarget.innerHTML = '<p class="text-gray-500 py-8 text-center">データがありません</p>'
      return
    }

    const range = XLSX.utils.decode_range(sheet["!ref"])
    const maxRow = Math.min(range.e.r, 49)
    const maxCol = range.e.c
    const merges = sheet["!merges"] || []

    let html = '<table class="min-w-full text-xs border-collapse">'
    html += '<thead class="sticky top-0 z-10"><tr class="bg-gray-100">'
    html += '<th class="px-2 py-1.5 text-center font-medium text-gray-400 border border-gray-300 bg-gray-100 min-w-[40px] sticky left-0 z-20"></th>'
    for (let c = 0; c <= maxCol; c++) {
      html += `<th class="px-2 py-1.5 text-center font-medium text-gray-400 border border-gray-300 bg-gray-100 min-w-[80px]">${XLSX.utils.encode_col(c)}</th>`
    }
    html += '</tr></thead><tbody>'

    for (let r = 0; r <= maxRow; r++) {
      html += '<tr>'
      html += `<td class="px-2 py-1 text-center border border-gray-300 font-mono bg-gray-50 text-gray-400 sticky left-0 z-[5]">${r + 1}</td>`
      for (let c = 0; c <= maxCol; c++) {
        if (this.isMergedHidden(merges, r, c)) continue
        const ref = XLSX.utils.encode_cell({ r, c })
        const cell = sheet[ref]
        const value = cell ? (cell.w || (cell.v != null ? String(cell.v) : "")) : ""
        const style = cell?.s ? this.cellStyleToCSS(cell.s) : ""
        const merge = this.getMergeSpan(merges, r, c)
        const spanAttrs = merge ? ` colspan="${merge.cs}" rowspan="${merge.rs}"` : ""
        html += `<td class="px-2 py-1 border border-gray-300 whitespace-nowrap"${spanAttrs} style="${style}">${this.escapeHtml(value)}</td>`
      }
      html += '</tr>'
    }

    html += '</tbody></table>'
    if (range.e.r > 49) {
      html += `<p class="text-xs text-gray-400 text-center py-2">先頭50行を表示しています（全${range.e.r + 1}行）</p>`
    }
    this.previewTarget.innerHTML = html
  }

  cellStyleToCSS(s) {
    if (!s) return ""
    let css = ""
    if (s.fgColor?.rgb) css += `background-color:#${s.fgColor.rgb};`
    if (s.bold) css += "font-weight:bold;"
    if (s.italic) css += "font-style:italic;"
    if (s.color?.rgb) css += `color:#${s.color.rgb};`
    return css
  }

  isMergedHidden(merges, r, c) {
    return merges.some(m => r >= m.s.r && r <= m.e.r && c >= m.s.c && c <= m.e.c && !(r === m.s.r && c === m.s.c))
  }

  getMergeSpan(merges, r, c) {
    const m = merges.find(m => m.s.r === r && m.s.c === c)
    if (!m) return null
    return { rs: m.e.r - m.s.r + 1, cs: m.e.c - m.s.c + 1 }
  }

  escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }

  loadingHtml() {
    const rows = Array.from({ length: 6 }, (_, i) =>
      `<div class="flex gap-3 animate-pulse" style="animation-delay: ${i * 0.08}s"><div class="h-3 w-10 rounded bg-gray-200 shrink-0"></div><div class="h-3 rounded bg-gray-200 flex-1"></div><div class="h-3 rounded bg-gray-200 flex-1"></div><div class="h-3 rounded bg-gray-200 flex-1"></div></div>`
    ).join("")
    return `<div class="px-4 py-10 space-y-4"><div class="flex justify-center mb-6"><div class="bounce-loader"><div class="dot"></div><div class="dot"></div><div class="dot"></div><div class="dot"></div><div class="dot"></div></div></div>${rows}</div>`
  }
}
